import {
  alpha,
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Container,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, ChildCare, Edit } from "@mui/icons-material";
import FormActions from "components/FormActions";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addChild as addChildApi,
  getClassrooms,
  getParents as getParentsApi,
} from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/fr";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { stringAvatar } from "utils";
import languages from "components/languages";

interface FormFields {
  firstName: string;
  lastName: string;
  gender: string;
  ethnicity: string;
  religion: string;
  languages: string;
  attendanceMode: string;
  fatherFirstName: string;
  fatherLastName: string;
  fatherEmail: string;
  fatherMobile: string;
  motherFirstName: string;
  motherLastName: string;
  motherEmail: string;
  motherMobile: string;
  thirdFirstName: string;
  thirdLastName: string;
  thirdEmail: string;
  thirdMobile: string;
  photo?: File;
}

type Classroom = { id: number; name: string; nurseryId: number };
type Parent = { id: number; firstName: string; lastName: string };

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const req = (label: string) => (
  <>
    {label} <span style={{ color: "#d32f2f" }}>*</span>
  </>
);

const AddChild: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { register, handleSubmit, watch, setValue } = useForm<FormFields>({
    defaultValues: { gender: "Male", attendanceMode: "0" },
  });
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState<File>();
  const [dob, setDob] = useState<Dayjs | null>(null);
  const [parentId, setParentId] = useState("");
  const [classroomId, setClassroomId] = useState("");
  const [parents, setParents] = useState<Parent[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);

  const childName = watch("firstName");

  useEffect(() => {
    Promise.all([getParentsApi(), getClassrooms()]).then(([pa, cl]) => {
      setParents(pa || []);
      setClassrooms(cl || []);
    });
  }, []);

  useEffect(() => {
    if (photo) setValue("photo", photo);
  }, [photo, setValue]);

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    if (!parentId) {
      enqueueSnackbar(t("placeholders.select_parent"), { variant: "warning" });
      return;
    }
    if (!classroomId) {
      enqueueSnackbar(t("placeholders.select_classroom"), {
        variant: "warning",
      });
      return;
    }
    if (!dob) {
      enqueueSnackbar(t("placeholders.select_dob"), { variant: "warning" });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    if (photo) formData.append("photo", photo);
    const { photo: _, ...rest } = data;
    const infos = {
      firstName: rest.firstName,
      lastName: rest.lastName,
      gender: rest.gender,
      address: "",
      ethnicity: rest.ethnicity,
      religion: rest.religion,
      languages: rest.languages,
      attendanceMode: Number(rest.attendanceMode ?? "0"),
      fname1: rest.fatherFirstName,
      lname1: rest.fatherLastName,
      email1: rest.fatherEmail,
      mobile1: rest.fatherMobile,
      fname2: rest.motherFirstName,
      lname2: rest.motherLastName,
      email2: rest.motherEmail,
      mobile2: rest.motherMobile,
      fname3: rest.thirdFirstName,
      lname3: rest.thirdLastName,
      email3: rest.thirdEmail,
      mobile3: rest.thirdMobile,
    };
    formData.append("infos", JSON.stringify(infos));
    formData.append("parentId", parentId);
    formData.append("classroomId", classroomId);
    formData.append("nurseryId", classrooms[0]?.nurseryId?.toString() || "");
    formData.append("dateOfBirth", dob?.toISOString() || "");

    addChildApi(formData)
      .then(() => {
        enqueueSnackbar(t("child.added"), { variant: "success" });
        navigate("/child");
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Failed", {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => navigate("/child")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <ChildCare sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("child.add")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Photo */}
          <Box display="flex" justifyContent="center" mb={3}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Fab
                  size="small"
                  component="label"
                  color="primary"
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                >
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.length) setPhoto(e.target.files[0]);
                    }}
                  />
                  <Edit sx={{ fontSize: 16 }} />
                </Fab>
              }
            >
              <Avatar
                src={photo ? URL.createObjectURL(photo) : undefined}
                {...(!photo
                  ? stringAvatar(childName || " ", {
                      width: 88,
                      height: 88,
                      fontSize: 32,
                      border: `3px solid ${theme.palette.divider}`,
                    })
                  : {
                      sx: {
                        width: 88,
                        height: 88,
                        fontSize: 32,
                        border: `3px solid ${theme.palette.divider}`,
                      },
                    })}
              />
            </Badge>
          </Box>

          {/* Child details */}
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.general_info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.first_name"))}
                    fullWidth
                    size="small"
                    {...register("firstName", { required: true })}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.last_name"))}
                    fullWidth
                    size="small"
                    {...register("lastName", { required: true })}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.gender"))}</InputLabel>
                    <Select
                      label={req(t("inputs.gender"))}
                      defaultValue="Male"
                      {...register("gender", { required: true })}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="Male">{t("gender.male")}</MenuItem>
                      <MenuItem value="Female">{t("gender.female")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={i18n.language}
                  >
                    <DesktopDatePicker
                      label={t("inputs.birthday")}
                      value={dob}
                      onChange={setDob}
                      inputFormat="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={inputSx}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.parent"))}</InputLabel>
                    <Select
                      label={req(t("inputs.parent"))}
                      value={parentId}
                      onChange={(e) => setParentId(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {parents.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.firstName} {p.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.classroom"))}</InputLabel>
                    <Select
                      label={req(t("inputs.classroom"))}
                      value={classroomId}
                      onChange={(e) => setClassroomId(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {classrooms.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("labels.attendance")}</InputLabel>
                    <Select
                      label={t("labels.attendance")}
                      defaultValue="0"
                      {...register("attendanceMode")}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="0">{t("labels.full_time")}</MenuItem>
                      <MenuItem value="1">{t("labels.part_time")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Additional details */}
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.additional_details")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label={t("inputs.ethnicity")}
                    fullWidth
                    size="small"
                    {...register("ethnicity")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={t("inputs.religion")}
                    fullWidth
                    size="small"
                    {...register("religion")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("labels.language")}</InputLabel>
                    <Select
                      label={t("labels.language")}
                      defaultValue=""
                      {...register("languages")}
                      sx={{ borderRadius: 2 }}
                    >
                      {languages.map(({ name, nativeName }) => (
                        <MenuItem key={name} value={name}>
                          {nativeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Contacts */}
          {[
            { label: "labels.father", prefix: "father" },
            { label: "labels.mother", prefix: "mother" },
            { label: "labels.other_contact", prefix: "third" },
          ].map(({ label, prefix }) => (
            <Card
              key={prefix}
              variant="outlined"
              sx={{ borderRadius: 3, mb: 3 }}
            >
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  {t(label)}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.first_name")}
                      fullWidth
                      size="small"
                      {...register(`${prefix}FirstName` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.last_name")}
                      fullWidth
                      size="small"
                      {...register(`${prefix}LastName` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.email")}
                      fullWidth
                      size="small"
                      type="email"
                      {...register(`${prefix}Email` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label={t("inputs.mobile")}
                      fullWidth
                      size="small"
                      {...register(`${prefix}Mobile` as any)}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          <FormActions
            onCancel={() => navigate("/child")}
            submitLabel={t("buttons.add")}
            submitting={submitting}
          />
        </form>
      </Container>
    </>
  );
};

export default AddChild;
